import { useMemo } from 'react';
import { getCurrentUser } from '../utils/RoleManager';
import { USER_ROLES, canAccess } from '../constants/userRoles';

/**
 * Hook para verificar permissões de acesso baseadas em papel
 * @returns {Object} - Objeto com funções de verificação de acesso
 */
export function useRoleAccess() {
  const user = getCurrentUser();
  
  return useMemo(() => ({
    /**
     * Verifica se o usuário tem um dos papéis permitidos
     * @param {Array<string>} allowedRoles - Papéis permitidos
     * @returns {boolean} - true se tem acesso, false caso contrário
     */
    hasRole: (allowedRoles) => {
      return allowedRoles.includes(user?.role);
    },

    /**
     * Verifica se o papel alcança uma área (ACCESS_AREAS em userRoles.js)
     * @param {string} area - Nome da área ('financial', 'dataExport'...)
     * @returns {boolean} - true se alcança, false caso contrário
     */
    canAccess: (area) => canAccess(user?.role, area),

    /**
     * Verifica se o usuário tem a marcação "Profissional de saúde" (abre prontuário,
     * atendimento e anexos). Independe do papel; só o proprietário liga.
     * @returns {boolean} - true se tem a marcação, false caso contrário
     */
    canAccessHealthData: () => !!user?.isHealthProfessional,

    /**
     * Verifica se o usuário está no alcance "Só os meus pacientes": vê só a própria agenda
     * e abre só os prontuários de quem passou por ela ou cadastrou (a API já filtra).
     * @returns {boolean} - true se o alcance é só o próprio, false se vê a clínica inteira
     */
    hasOwnScheduleScope: () => !!user?.isHealthProfessional && user?.scheduleScope === 'own',
    
    /**
     * Verifica se o usuário é proprietário da empresa
     * @returns {boolean} - true se for proprietário, false caso contrário
     */
    isOwner: () => user?.role === USER_ROLES.COMPANY_OWNER,
    
    /**
     * Verifica se o usuário é administrador da empresa
     * @returns {boolean} - true se for administrador, false caso contrário
     */
    isAdmin: () => user?.role === USER_ROLES.COMPANY_ADMIN,
    
    /**
     * Verifica se o usuário é proprietário ou administrador
     * @returns {boolean} - true se for proprietário ou administrador, false caso contrário
     */
    isOwnerOrAdmin: () => {
      return [USER_ROLES.COMPANY_OWNER, USER_ROLES.COMPANY_ADMIN].includes(user?.role);
    },
    
    /**
     * Obtém o papel atual do usuário
     * @returns {string|null} - Papel do usuário ou null se não estiver logado
     */
    getCurrentRole: () => user?.role || null
  }), [user]);
}

export default useRoleAccess;